<template>
  <div>
    <PhysicalAddressCard
      class="mb-4"
      :item="peParty.physical_address"
    />

    <div>
      <ResidenceHistoriesCard
        v-for="residence_history of chronologicallyOrderedResidenceHistories"
        :key="residence_history.id"
        :item="residence_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ResidenceHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('residence-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Residence History"
      :has-new-section="peResidenceHistoriesCardHasNewSection"
      @on-add="peResidenceHistoriesCardAddSection"
    />

    <RelativeSectionAdderLine
      title="Residence History"
      property="residence_histories"
      @on-add="peResidenceHistoriesMultipleCardCreate"
    />

    <div>
      <JobHistoriesCard
        v-for="job_history of peParty.job_histories"
        :key="job_history.id"
        :item="job_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <JobHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('job-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Job History"
      :has-new-section="peJobHistoriesCardHasNewSection"
      @on-add="peJobHistoriesCardAddSection"
    />

    <RelativeSectionAdderLine
      title="Job History"
      property="job_histories"
      @on-add="peJobHistoriesMultipleCardCreate"
    />

    <div>
      <EducationHistoriesCard
        v-for="education_history of peParty.education_histories"
        :key="education_history.id"
        :item="education_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <EducationHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('education-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Education History"
      :has-new-section="peEducationHistoriesCardHasNewSection"
      @on-add="peEducationHistoriesCardAddSection"
    />

    <div>
      <CriminalHistoriesCard
        v-for="criminal_history of peParty.criminal_histories"
        :key="criminal_history.id"
        :item="criminal_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <CriminalHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('criminal-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Criminal History"
      :has-new-section="peCriminalHistoriesCardHasNewSection"
      @on-add="peCriminalHistoriesCardAddSection"
    />

    <div>
      <DetentionHistoriesCard
        v-for="detention_history of peParty.detention_histories"
        :key="detention_history.id"
        :item="detention_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <DetentionHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('detention-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Detention History"
      :has-new-section="peDetentionHistoriesCardHasNewSection"
      @on-add="peDetentionHistoriesCardAddSection"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'

import CriminalHistoriesCard from './SectionCards/HistoryCards/CriminalHistoriesCard'
import DetentionHistoriesCard from './SectionCards/HistoryCards/DetentionHistoriesCard'
import EducationHistoriesCard from './SectionCards/HistoryCards/EducationHistoriesCard'
import JobHistoriesCard from './SectionCards/HistoryCards/JobHistoriesCard'
import PhysicalAddressCard from './SectionCards/PersonalDataCards/PhysicalAddressCard'
import RelativeSectionAdderLine from './SectionTools/RelativeSectionAdderLine.vue'
import ResidenceHistoriesCard from './SectionCards/HistoryCards/ResidenceHistoriesCard'
import SectionAdderLine from './SectionTools/SectionAdderLine'

export default {
  name: 'PEHistory',

  components: {
    CriminalHistoriesCard,
    DetentionHistoriesCard,
    EducationHistoriesCard,
    JobHistoriesCard,
    PhysicalAddressCard,
    RelativeSectionAdderLine,
    ResidenceHistoriesCard,
    SectionAdderLine,
  },

  beforeRouteLeave (_, __, next) {
    if (this.peAnyUnlockedSections) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.peLockPartySections()
        next()
      } else next(false)
    } else {
      this.peLockPartySections()
      next()
    }
  },

  props: {
    id: {
      type: String || Number,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  computed: {
    chronologicallyOrderedResidenceHistories () {
      const unsorted = this.peParty.residence_histories

      return unsorted.sort((a, b) => {
        if (a.end_date && b.end_date) {
          if (a.end_date < b.end_date) return 1
          if (a.end_date > b.end_date) return -1
        } else if (a.start_date && b.start_date) {
          if (a.start_date < b.start_date) return 1
          if (a.start_date > b.start_date) return -1
        }

        return 0
      })
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAnyUnlockedSections',
      'peCriminalHistoriesCardHasNewSection',
      'peDetentionHistoriesCardHasNewSection',
      'peEducationHistoriesCardHasNewSection',
      'peJobHistoriesCardHasNewSection',
      'peParty',
      'peResidenceHistoriesCardHasNewSection',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'partySurveyCardsByIdentifiers',
      'peSurveysValidationActive',
    ]),
  },

  created () {
    this.peGetPartySection({ id: this.id }).
      catch(() => this.displayAlert('error', `No Party found with the id: ${this.id}`))
  },

  methods: {
    ...mapActions(PARTY_NAMESPACE, [
      'peCriminalHistoriesCardAddSection',
      'peDetentionHistoriesCardAddSection',
      'peEducationHistoriesCardAddSection',
      'peGetPartySection',
      'peJobHistoriesCardAddSection',
      'peJobHistoriesMultipleCardCreate',
      'peLockPartySections',
      'peResidenceHistoriesCardAddSection',
      'peResidenceHistoriesMultipleCardCreate',
    ]),
  },
}
</script>
